import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import { getAllBlogPosts } from "../services/blogService";

const Blog = () => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState("all");
    const blogPosts = useMemo(() => getAllBlogPosts(), []);

    const categories = [
        { id: "all", name: t("blog_page.categories.all"), count: blogPosts.length },
        {
            id: "form-assistance",
            name: t("blog_page.categories.form_assistance"),
            count: blogPosts.filter((post) => post.category === "form-assistance").length,
        },
        {
            id: "green-card",
            name: t("blog_page.categories.green_card"),
            count: blogPosts.filter((post) => post.category === "green-card")
                .length,
        },
        {
            id: "citizenship",
            name: t("blog_page.categories.citizenship"),
            count: blogPosts.filter((post) => post.category === "citizenship").length,
        },
        {
            id: "visas",
            name: t("blog_page.categories.visas"),
            count: blogPosts.filter((post) => post.category === "visas")
                .length,
        },
        {
            id: "immigration-law",
            name: t("blog_page.categories.immigration_law"),
            count: blogPosts.filter((post) => post.category === "immigration-law")
                .length,
        },
    ];

    const filteredPosts =
        selectedCategory === "all"
            ? blogPosts
            : blogPosts.filter((post) => post.category === selectedCategory);

    const featuredPost = blogPosts.find((post) => post.featured);
    const regularPosts = filteredPosts.filter((post) => !post.featured);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <section className="relative isolate overflow-hidden text-white py-24 md:py-32">
                {/* dynamic gradient bg */}
                <div className="absolute inset-0 -z-30 animate-[gradientMove_16s_ease_infinite] bg-[linear-gradient(120deg,#1e3a8a_0%,#1d4ed8_50%,#2563eb_100%)] bg-[length:200%_200%] opacity-95" />

                {/* floating balls (glossy blobs) */}
                <div className="pointer-events-none absolute inset-0 -z-20">
                    <span className="ball absolute left-[6%] top-[18%]" />
                    <span className="ball absolute right-[10%] top-[28%] delay-300" />
                    <span className="ball absolute left-[15%] bottom-[18%] delay-700" />
                    <span className="ball-sm absolute right-[22%] bottom-[26%] delay-500" />
                    <span className="ball-sm absolute left-[28%] top-[36%] delay-1000" />
                </div>

                {/* subtle radial lights */}
                <div
                    aria-hidden
                    className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-white/10 blur-3xl -z-10"
                    style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
                />
                <div
                    aria-hidden
                    className="absolute -bottom-32 -right-32 h-[34rem] w-[34rem] rounded-full bg-white/10 blur-3xl -z-10"
                    style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
                />

                {/* content */}
                <div className="relative max-w-6xl mx-auto px-6 sm:px-8 text-center">
                    <motion.div
                        className="inline-flex items-center gap-2 mb-6 rounded-full border border-white/20 bg-white/10 px-6 py-2 backdrop-blur-md"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-[pingSlow_2.4s_linear_infinite]" />
                        <span className="text-sm font-semibold tracking-wide text-white/90">
                            {t("blog_page.hero.badge")}
                        </span>
                    </motion.div>

                    <motion.h1
                        className="text-5xl md:text-6xl font-black tracking-tight leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,.35)]"
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05, duration: 0.65 }}
                        style={{
                            background:
                                "linear-gradient(180deg, #ffffff 0%, #e2e8f0 60%, rgba(255,255,255,.85) 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        {t("blog_page.hero.title")}
                    </motion.h1>

                    {/* glowing animated underline */}
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 0.25, duration: 0.7 }}
                        className="relative mx-auto mt-4 h-[3px] w-48 origin-center rounded-full bg-gradient-to-r from-cyan-300 via-white to-blue-300 shadow-[0_0_18px_rgba(255,255,255,.6)] overflow-hidden"
                    >
                        <div className="absolute inset-0 shimmer opacity-80" />
                        <div className="absolute -inset-1 bg-white/40 blur-md glowPulse rounded-full" />
                    </motion.div>

                    <motion.p
                        className="mt-6 text-lg md:text-[1.25rem] leading-relaxed text-blue-100/90 max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        {t("blog_page.hero.description")}
                    </motion.p>
                </div>

                {/* animated layered waves */}
                <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none -z-10">
                    <svg viewBox="0 0 1440 240" className="w-full h-[110px] md:h-[150px]" preserveAspectRatio="none">
                        <path
                            className="animate-[waveDrift_14s_ease_in_out_infinite]"
                            fill="rgba(255,255,255,0.20)"
                            d="M0,140 C180,160 360,120 540,140 C720,160 900,200 1080,170 C1260,140 1350,120 1440,130 L1440,240 L0,240 Z"
                        />
                        <path
                            className="animate-[waveDrift_12s_ease_in_out_infinite_reverse]"
                            fill="rgba(255,255,255,0.28)"
                            d="M0,150 C200,130 420,170 640,155 C860,140 1000,150 1220,160 C1330,165 1385,165 1440,166 L1440,240 L0,240 Z"
                        />
                        <path
                            className="animate-[waveDrift_10s_ease_in_out_infinite]"
                            fill="rgba(255,255,255,0.36)"
                            d="M0,165 C240,170 420,160 620,168 C820,176 1040,154 1240,162 C1340,166 1390,166 1440,166 L1440,240 L0,240 Z"
                        />
                    </svg>
                </div>

                {/* keyframes + balls styles */}
                <style jsx>{`
    @keyframes gradientMove {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes waveDrift {
      0% { transform: translateX(0px) translateY(0px); }
      50% { transform: translateX(-20px) translateY(6px); }
      100% { transform: translateX(0px) translateY(0px); }
    }
    @keyframes pingSlow {
      0% { transform: scale(0.85); opacity: 1; }
      70% { transform: scale(1.25); opacity: 0; }
      100% { transform: scale(0.85); opacity: 1; }
    }
    @keyframes shimmer {
      0% { transform: translateX(-120%); }
      100% { transform: translateX(120%); }
    }
    @keyframes glowPulse {
      0%,100% { opacity: .45; }
      50% { opacity: .85; }
    }
    .shimmer {
      background: linear-gradient(90deg, transparent, rgba(255,255,255,.95), transparent);
      animation: shimmer 2.2s ease-in-out infinite;
    }
    .glowPulse {
      animation: glowPulse 2.8s ease-in-out infinite;
    }
    @keyframes floatA {
      0% { transform: translateY(0) translateX(0) scale(1); }
      50% { transform: translateY(-16px) translateX(10px) scale(1.03); }
      100% { transform: translateY(0) translateX(0) scale(1); }
    }
    @keyframes floatB {
      0% { transform: translateY(0) translateX(0) scale(1); }
      50% { transform: translateY(14px) translateX(-12px) scale(0.98); }
      100% { transform: translateY(0) translateX(0) scale(1); }
    }
    .ball {
      width: 220px; height: 220px; border-radius: 9999px;
      background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.55), rgba(255,255,255,.12) 60%, rgba(255,255,255,0) 70%);
      filter: blur(2px);
      mix-blend-mode: screen;
      animation: floatA 9s ease-in-out infinite;
    }
    .ball-sm {
      width: 130px; height: 130px; border-radius: 9999px;
      background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.45), rgba(255,255,255,.1) 60%, rgba(255,255,255,0) 70%);
      filter: blur(1px);
      mix-blend-mode: screen;
      animation: floatB 11s ease-in-out infinite;
    }
    .delay-300 { animation-delay: .3s !important; }
    .delay-500 { animation-delay: .5s !important; }
    .delay-700 { animation-delay: .7s !important; }
    .delay-1000 { animation-delay: 1s !important; }
  `}</style>
            </section>



            {/* Category Filter */}
            <section className="py-8 bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ScrollAnimation direction="up" delay={0.1}>
                        <div className="flex flex-wrap gap-4 justify-center">
                            {categories.map((category, index) => (
                                <motion.button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${selectedCategory === category.id
                                        ? "bg-blue-600 text-white shadow-lg"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        }`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1, duration: 0.6 }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {category.name} ({category.count})
                                </motion.button>
                            ))}
                        </div>
                    </ScrollAnimation>
                </div>
            </section>

            {/* Featured Post */}
            {featuredPost && selectedCategory === "all" && (
                <section className="py-16 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <ScrollAnimation direction="up" delay={0.1}>
                            <motion.div
                                className="mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2, duration: 0.6 }}
                            >
                                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                    {t("blog_page.featured.title")}
                                </h2>
                                <p className="text-gray-600">
                                    {t("blog_page.featured.subtitle")}
                                </p>
                            </motion.div>
                        </ScrollAnimation>

                        <ScrollAnimation direction="up" delay={0.2}>
                            <LanguageLink to={`/blog/${featuredPost.id}`}>
                                <motion.div
                                    className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl overflow-hidden shadow-xl cursor-pointer"
                                    whileHover={{ scale: 1.02, y: -5 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                                        <motion.div
                                            className="p-8 lg:p-12"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.3, duration: 0.6 }}
                                        >
                                            <div className="flex items-center space-x-4 mb-4">
                                                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                    {t("blog_page.featured.badge")}
                                                </span>
                                                <span className="text-gray-500 text-sm">
                                                    {featuredPost.readTime}
                                                </span>
                                            </div>

                                            <h3 className="text-3xl font-bold text-gray-900 mb-4">
                                                {featuredPost.title}
                                            </h3>

                                            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                                                {featuredPost.excerpt}
                                            </p>

                                            <motion.div
                                                className="flex items-center justify-between"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.4, duration: 0.6 }}
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <div>
                                                        <p className="font-medium text-gray-900">
                                                            {featuredPost.author}
                                                        </p>
                                                        <p className="text-gray-500 text-sm">
                                                            {featuredPost.date}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div>
                                                    <span className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200 inline-block">
                                                        {t("blog_page.featured.read_more")}
                                                    </span>
                                                </div>
                                            </motion.div>
                                        </motion.div>

                                        <motion.div
                                            className="h-80 lg:h-auto relative overflow-hidden"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.5, duration: 0.6 }}
                                        >
                                            <img
                                                src={featuredPost.image}
                                                alt={featuredPost.title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/20"></div>
                                        </motion.div>
                                    </div>
                                </motion.div>
                            </LanguageLink>
                        </ScrollAnimation>
                    </div>
                </section>
            )}

            {/* Blog Posts Grid */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ScrollAnimation direction="up" delay={0.1}>
                        <motion.div
                            className="mb-12"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                {selectedCategory === "all"
                                    ? t("blog_page.list.all_articles")
                                    : categories.find((c) => c.id === selectedCategory)?.name}
                            </h2>
                            <p className="text-gray-600">
                                {filteredPosts.length} {t("blog_page.list.articles_found")}
                            </p>
                        </motion.div>
                    </ScrollAnimation>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {regularPosts.map((post, index) => (
                            <ScrollAnimation key={post.id} direction="up" delay={index * 0.1}>
                                <LanguageLink to={`/blog/${post.id}`}>
                                    <motion.article
                                        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                                        whileHover={{ scale: 1.02, y: -5 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {/* Post Image */}
                                        <motion.div
                                            className="h-48 relative overflow-hidden"
                                            whileHover={{ scale: 1.05 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <img
                                                src={post.image}
                                                alt={post.title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/10"></div>

                                            {/* Category Badge */}
                                            <div className="absolute top-4 left-4">
                                                <span className="bg-white/90 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                                                    {categories.find((c) => c.id === post.category)?.name}
                                                </span>
                                            </div>

                                            {/* Read Time */}
                                            <div className="absolute top-4 right-4">
                                                <span className="bg-white/90 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                                                    {post.readTime}
                                                </span>
                                            </div>
                                        </motion.div>

                                        {/* Post Content */}
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                                                {post.title}
                                            </h3>

                                            <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                                                {post.excerpt}
                                            </p>

                                            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                                                <span>{post.author}</span>
                                                <span>{post.date}</span>
                                            </div>

                                            <div className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors">
                                                {t("blog_page.list.read_more")}
                                                <svg
                                                    className="w-4 h-4 ml-1"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 5l7 7-7 7"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </motion.article>
                                </LanguageLink>
                            </ScrollAnimation>
                        ))}
                    </div>

                    {filteredPosts.length === 0 && (
                        <ScrollAnimation direction="up" delay={0.1}>
                            <motion.div
                                className="text-center py-12"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2, duration: 0.6 }}
                            >
                                <motion.svg
                                    className="w-16 h-16 text-gray-400 mx-auto mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.3, duration: 0.5 }}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </motion.svg>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    {t("blog_page.list.no_articles.title")}
                                </h3>
                                <p className="text-gray-600">
                                    {t("blog_page.list.no_articles.text")}
                                </p>
                            </motion.div>
                        </ScrollAnimation>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Blog;
